import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout, PageHero } from "@/components/site/Layout";
import { Seo } from "@/components/site/Seo";
import { getAllPosts, BlogPost } from "@/lib/blog";
import { useLp, useUrlLocale } from "@/lib/localePath";
import { useI18n, pick } from "@/lib/i18n";
import { Calendar, User, ArrowRight } from "lucide-react";
import contactHeroImage from "@/assets/atasehir-skyline.webp";

export default function BlogIndex() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const lp = useLp();
  const { t } = useI18n();
  // BlogPost.tsx'teki aynı yarış durumu burada da geçerli — bkz. oradaki yorum.
  const locale = useUrlLocale() ?? "tr";

  useEffect(() => {
    setLoading(true);
    getAllPosts(locale)
      .then(setPosts)
      .finally(() => setLoading(false));
  }, [locale]);

  return (
    <Layout>
      <Seo
        title={pick(locale, {
          tr: "Blog — Anadolu Terra | Güvenlik & Tesis Yönetimi",
          en: "Blog — Anadolu Terra | Security & Facility Management",
        })}
        description={pick(locale, {
          tr: "Anadolu Terra blog sayfası. Güvenlik sektörü, yapay zeka, GEO, dijital dönüşüm ve tesis yönetimi hakkında güncel yazılar.",
          en: "The Anadolu Terra blog. Current articles on the security industry, artificial intelligence, GEO, digital transformation and facility management.",
        })}
        path="/blog"
      />

      {/* Hero */}
      <PageHero
        title={pick(locale, { tr: "Blog", en: "Blog" })}
        subtitle={pick(locale, {
          tr: "Güvenlik sektörü, yapay zeka, dijital dönüşüm ve tesis yönetimi hakkında güncel yazılar.",
          en: "Current articles on the security industry, artificial intelligence, digital transformation and facility management.",
        })}
        image={contactHeroImage}
        overlay="light"
      />

      {/* Posts */}
      <section className="py-16 md:py-24">
        <div className="container-page">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-background p-6 animate-pulse"
                >
                  <div className="h-48 bg-muted rounded-xl mb-4" />
                  <div className="h-4 bg-muted rounded w-1/3 mb-3" />
                  <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-full mb-1" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <h2 className="text-2xl font-bold text-foreground/60">
                {pick(locale, {
                  tr: "Henüz blog yazısı bulunmuyor.",
                  en: "No blog posts available yet in this language.",
                })}
              </h2>
              <p className="mt-2 text-foreground/40">
                {pick(locale, {
                  tr: "Çok yakında yeni yazılarımız yayında olacak.",
                  en: "New articles will be published here soon.",
                })}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={lp(`/blog/${post.slug}`)}
                  className="group rounded-2xl border border-border bg-background overflow-hidden transition-all duration-300 hover:shadow-elevated hover:-translate-y-1"
                >
                  <div className="aspect-[16/9] relative overflow-hidden bg-gradient-to-br from-accent via-accent/90 to-ink flex flex-col justify-between p-6">
                    {/* dekoratif çizgiler */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 right-0 w-40 h-40 rounded-full border-[24px] border-white translate-x-16 -translate-y-16" />
                      <div className="absolute bottom-0 left-0 w-28 h-28 rounded-full border-[16px] border-white -translate-x-10 translate-y-10" />
                    </div>
                    <span className="relative z-10 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-widest uppercase text-white/60">
                      Anadolu Terra
                    </span>
                    <p className="relative z-10 text-white font-bold font-heading text-lg leading-snug line-clamp-3 drop-shadow">
                      {post.title}
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-foreground/50 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        {post.author}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-foreground font-heading tracking-tight group-hover:text-accent transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground/70 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-sm font-medium text-accent group-hover:gap-2 transition-all">
                      {t("common.readMore")}{" "}
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
